import {
    useCustomQuery,
    useCustomMutation,
    useCustomerType,
    useCustomerId
} from '@salesforce/commerce-sdk-react'
import {useMemo} from 'react'
import {keysToCamel} from '../commerce-api/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from './use-multi-site'
import {useCurrentCustomer} from './use-current-customer'
import useImprovedCustomMutation from './useImprovedCustomMutation'
import {createReportingFetch} from '../commerce-api/utils'
import {app as appConfig} from '../../config/default'

const getCustomObjectResult = (data) => data?.c_result || data || {}

/**
 * Custom API to check if a customer exists by email.
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useShopperObjectsCustomerExistsMutation = (mutationOptions) => {
    const {locale} = useMultiSite()
    return useImprovedCustomMutation(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-customer',
                    endpointPath: 'customers/exists'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        mutationOptions
    )
}

/**
 * Custom API to get the storefront reporting event URL.
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useShopperObjectsReportingUrlMutation = (mutationOptions) => {
    const {locale} = useMultiSite()
    const reportingFetch = useMemo(() => {
        const apiConfig = {...appConfig.commerceAPI, proxy: undefined}
        return createReportingFetch(apiConfig)
    }, [])

    return useImprovedCustomMutation(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-storefront',
                    endpointPath: 'reporting/url'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        {
            ...mutationOptions,
            onSuccess: (data, variables, context) => {
                const reportURL = data?.c_result?.url || data?.url
                if (reportURL) {
                    reportingFetch(reportURL)
                }
                mutationOptions?.onSuccess?.(data, variables, context)
            }
        }
    )
}

/**
 * Custom API to validate the current basket.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult}
 */
export const useShopperObjectsBasketValidation = (queryOptions) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-checkout',
                    endpointPath: 'basket/validation'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        {
            ...queryOptions,
            meta: {displayName: 'useShopperObjectsBasketValidation', ...queryOptions?.meta},
            select: (data) => getCustomObjectResult(data)
        }
    )
}

useShopperObjectsBasketValidation.queryKey = () => [
    '/commerce-sdk-react',
    '/custom',
    '/shopper-checkout',
    '/v1',
    '/organizations',
    `/${getConfig().app.commerceAPI.parameters.organizationId}`,
    '/basket/validation'
]

/**
 * Custom API mutation to validate the current basket from imperative flows.
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useShopperObjectsBasketValidationMutation = (mutationOptions) => {
    const {locale} = useMultiSite()
    return useImprovedCustomMutation(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-checkout',
                    endpointPath: 'basket/validation'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        mutationOptions
    )
}

/**
 * Custom API to get order number by basket token.
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useShopperObjectsOrderByTokenMutation = (mutationOptions) => {
    const {locale} = useMultiSite()
    return useImprovedCustomMutation(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-checkout',
                    endpointPath: 'orders/by-token'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        mutationOptions
    )
}

/**
 * Custom API to get site configuration data.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult}
 */
export const useShopperObjectsSite = (queryOptions) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-storefront',
                    endpointPath: 'site'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useShopperObjectsSite', ...queryOptions?.meta},
            select: (data) => (data?.c_result ? data : {c_result: keysToCamel(data)})
        }
    )
}

/**
 * Query key `useCustomQuery` builds for a custom API endpoint. Callers that read
 * from, write to, or invalidate a custom API cache entry must build the same key.
 *
 * @param {{apiName: string, apiVersion: string, endpointPath: string, organizationId: string}} customApiPathParameters
 * @param {Object} [parameters] - Request parameters, part of the key as received.
 * @returns {Array} The react-query key.
 */
const getCustomQueryKey = (
    {apiName, apiVersion, endpointPath, organizationId},
    parameters = undefined
) => [
    '/commerce-sdk-react',
    '/custom',
    `/${apiName}`,
    `/${apiVersion}`,
    '/organizations',
    `/${organizationId}`,
    `/${endpointPath}`,
    ...(parameters ? [parameters] : [])
]

const NAVIGATION_API = {apiName: 'shopper-navigation', apiVersion: 'v1'}
const NAVIGATION_ENDPOINT = {...NAVIGATION_API, endpointPath: 'navigation'}
const NAVIGATION_HEADER_ENDPOINT = {...NAVIGATION_API, endpointPath: 'navigation-header'}

const navigationParameters = (locale) => ({locale})

/**
 * Hook to get the Xleap navigation tree: the `leap-navigation-level1` module
 * with each `elements[].item.level2Module` already resolved. Backed by the
 * `shopper-navigation` `navigation` endpoint, where the backend's
 * `navigation` function performs the `level2ModuleId` join server-side, so the
 * module arrives stitched and unmapped — callers use it as received.
 *
 * @param {{parameters?: Object}} [apiOptions] - Extra request parameters.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult<{
 *   moduleId: string,
 *   pageId: string,
 *   moduleType: string,
 *   elements: Array<{item: {type: string, title: string, level2ModuleId?: string, level2Module?: Object}}>,
 *   teasers: Array<Object>
 * }, unknown>}
 *
 * @example
 * function FlyoutNavigation() {
 *   const {data} = useNavigationContent()
 *   // data.elements[0].item.title: 'Highchairs'
 *   // data.elements[0].item.level2Module: the resolved leap-navigation-level2 module
 *   return data?.elements?.map(({item}) => <NavigationParent key={item.title} item={item} />)
 * }
 */
export const useNavigationContent = (apiOptions, queryOptions) => {
    const {
        locale: {id: locale}
    } = useMultiSite()

    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: NAVIGATION_ENDPOINT,
                parameters: {
                    ...navigationParameters(locale),
                    ...apiOptions?.parameters
                }
            },
            rawResponse: false
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useNavigationContent', ...queryOptions?.meta}
        }
    )
}

/**
 * Query key of the default `useNavigationContent` request, for seeding or
 * invalidating its cache entry from outside the hook.
 *
 * @param {{organizationId: string, locale: string}} params
 * @returns {Array} The react-query key.
 */
useNavigationContent.queryKey = ({organizationId, locale}) =>
    getCustomQueryKey({...NAVIGATION_ENDPOINT, organizationId}, navigationParameters(locale))

/**
 * Hook to get the Xleap navigation header content: the CMS-provided "Products"
 * main-link label and the regular header links. Backed by the
 * `shopper-navigation` `navigation-header` endpoint. Returns the raw
 * `leap-header` module object directly, unmapped — callers derive display
 * shape themselves.
 *
 * @param {{parameters?: Object}} [apiOptions] - Extra request parameters.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult<{
 *   moduleId: string,
 *   pageId: string,
 *   moduleType: string,
 *   headerMainLink: {title: string},
 *   elements: Array<{title: string, accentColor: boolean, link: {type: string, value: string}}>
 * }, unknown>}
 */
export const useNavigationHeaderContent = (apiOptions, queryOptions) => {
    const {
        locale: {id: locale}
    } = useMultiSite()

    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: NAVIGATION_HEADER_ENDPOINT,
                parameters: {
                    ...navigationParameters(locale),
                    ...apiOptions?.parameters
                }
            },
            rawResponse: false
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useNavigationHeaderContent', ...queryOptions?.meta}
        }
    )
}

/**
 * Query key of the default `useNavigationHeaderContent` request, for seeding or
 * invalidating its cache entry from outside the hook.
 *
 * @param {{organizationId: string, locale: string}} params
 * @returns {Array} The react-query key.
 */
useNavigationHeaderContent.queryKey = ({organizationId, locale}) =>
    getCustomQueryKey({...NAVIGATION_HEADER_ENDPOINT, organizationId}, navigationParameters(locale))

export const useProductSetFullPrices = (apiOptions, queryOptions) => {
    const {
        site,
        locale: {id: localeId}
    } = useMultiSite()
    const preferredCurrency = useMemo(() => {
        return site?.l10n?.supportedLocales.find(
            (locale) => locale.id === localeId || locale.alias === localeId
        )?.preferredCurrency
    }, [site, localeId])
    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()
    return useCustomQuery(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'full-prices',
                    apiName: 'shopper-productset'
                },
                parameters: {
                    c_customerId: isRegistered ? customerId : 'guest',
                    c_currency: preferredCurrency,
                    locale: localeId,
                    ...apiOptions.parameters
                }
            },
            rawResponse: false
        },
        {
            ...queryOptions,
            meta: {displayName: 'useProductSetFullPrices', ...queryOptions?.meta},
            select: (data) => keysToCamel(data)
        }
    )
}

export const useProductSetPrice = (apiOptions, queryOptions) => {
    const {
        locale: {id: locale}
    } = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'price',
                    apiName: 'shopper-productset'
                },
                parameters: {
                    locale,
                    ...apiOptions.parameters
                }
            },
            rawResponse: false
        },
        {
            ...queryOptions,
            meta: {displayName: 'useProductSetPrice', ...queryOptions?.meta},
            select: (data) => keysToCamel(data)
        }
    )
}

export const usePaymentIcons = (apiOptions, queryOptions) => {
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'payment-icons',
                    apiName: 'shopper-storefront'
                },
                parameters: apiOptions.parameters
            },
            rawResponse: false
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'usePaymentIcons', ...queryOptions?.meta}
        }
    )
}

export const useCustomerActivation = (apiOptions, queryOptions) =>
    useCustomQuery(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'activate',
                    apiName: 'shopper-customer'
                },
                parameters: apiOptions.parameters
            },
            rawResponse: false
        },
        {
            ...queryOptions,
            meta: {displayName: 'useCustomerActivation', ...queryOptions?.meta}
        }
    )

/**
 * @param {{parameters?: Object, body: Object}}apiOptions
 * @param {import("@tanstack/react-query").MutationOptions} mutationOptions
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useCustomerResetPassword = (apiOptions, mutationOptions) =>
    useCustomMutation(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'reset-password',
                    apiName: 'shopper-customer'
                },
                parameters: apiOptions.parameters,
                body: JSON.stringify(apiOptions.body)
            },
            rawResponse: false
        },
        mutationOptions
    )

/**
 * @param {{parameters?: Object, body: Object}}apiOptions
 * @param {import("@tanstack/react-query").MutationOptions} mutationOptions
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useCustomerNewsletter = (apiOptions, mutationOptions) => {
    const {isRegistered} = useCustomerType()
    const {locale, site} = useMultiSite()
    const {data: customer} = useCurrentCustomer()
    const parameters = {
        ...apiOptions?.parameters,
        siteId: site.id,
        locale: locale.id
    }
    if (isRegistered) {
        parameters.c_customerNo = customer.customerNo
    }
    return useImprovedCustomMutation(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'newsletter',
                    apiName: 'shopper-customer'
                },
                parameters,
                body: apiOptions?.body || {}
            },
            rawResponse: false
        },
        mutationOptions
    )
}

export const useCustomerGetShippingPromotionMessages = (apiOptions, queryOptions) =>
    useCustomMutation(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'get-shipping-promotion-messages',
                    apiName: 'shopper-customer'
                },
                parameters: apiOptions.parameters
            },
            rawResponse: false
        },
        queryOptions
    )

/**
 * Hook to get customer warranties
 * @param {{locale?: string}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} queryOptions
 * @returns {import("@tanstack/react-query").UseQueryResult}
 */
export const useCustomerWarranties = (apiOptions, queryOptions) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'warranties',
                    apiName: 'shopper-customer'
                },
                parameters: {
                    locale: locale.id,
                    ...apiOptions.parameters
                }
            },
            rawResponse: false
        },
        {
            staleTime: 1 * 60 * 60 * 1000, // 1 hour
            ...queryOptions,
            meta: {displayName: 'useCustomerWarranties', ...queryOptions?.meta}
        }
    )
}

/**
 * Hook to send checkout trace data.
 * @param {{parameters: {locale: string}, body?: object}} apiOptions
 * @param {import("@tanstack/react-query").MutationOptions} mutationOptions
 * @returns {import("@tanstack/react-query").UseMutationResult<void, unknown, import("@salesforce/commerce-sdk-react").TMutationVariables, unknown>}
 */
export const useShopperCheckoutTrace = (apiOptions, mutationOptions) => {
    return useImprovedCustomMutation(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'trace',
                    apiName: 'shopper-checkout'
                },
                parameters: apiOptions?.parameters,
                body: apiOptions?.body ? JSON.stringify(apiOptions.body) : undefined
            },
            rawResponse: false
        },
        mutationOptions
    )
}

/**
 * @param {{parameters: {pageType: 'folder'|'content', pageId: string}}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} queryOptions
 * @returns {import("@tanstack/react-query").UseQueryResult}
 */
export const useCustomerServicePage = (apiOptions, queryOptions) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-customer-service',
                    endpointPath: `pages/${apiOptions.parameters.pageType}/${apiOptions.parameters.pageId}`
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useCustomerServicePage', ...queryOptions?.meta}
        }
    )
}

/**
 * @param {{body: Object}} apiOptions
 * @param {import("@tanstack/react-query").UseMutationOptions} mutationOptions
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useCustomerServiceContactMutation = (apiOptions, mutationOptions) => {
    const {locale} = useMultiSite()
    return useCustomMutation(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-customer-service',
                    endpointPath: `contact`
                },
                body: JSON.stringify(apiOptions?.body || {}),
                parameters: {
                    locale: locale.id,
                    ...apiOptions?.parameters
                }
            },
            rawResponse: false
        },
        mutationOptions
    )
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} mutationOptions
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useCustomerServiceDeclarationOfWithdrawalMutation = (mutationOptions) => {
    const {locale} = useMultiSite()
    return useImprovedCustomMutation(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-customer-service',
                    endpointPath: 'declaration-of-withdrawal'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        mutationOptions
    )
}

/**
 * @param {{body: {country: string, email: string, subject: string, message: string}}} apiOptions
 * @param {import("@tanstack/react-query").UseMutationOptions} mutationOptions
 * @returns {import("@tanstack/react-query").UseMutationResult}
 */
export const useCustomerServicePressContactMutation = (apiOptions, mutationOptions) => {
    const {locale} = useMultiSite()
    return useCustomMutation(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-customer-service',
                    endpointPath: `press-contact`
                },
                body: JSON.stringify(apiOptions?.body || {}),
                parameters: {
                    locale: locale.id,
                    ...apiOptions?.parameters
                }
            },
            rawResponse: false
        },
        mutationOptions
    )
}

/**
 * Custom api similar to https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-seo?meta=getUrlMapping
 * but also resolving content assets and folders. Response structure is the same as in the above link.
 *
 * **Note:** in case the url can not be resolved the original API returns a 404 while our custom API returns a 200 but with the response body:
 * ```json
 * {}
 * ```
 *
 * Example response for assets:
 * ```json
 * {
 *     "resourceId": "counterfeit",
 *     "resourceSubType": "standard_content_asset",
 *     "resourceType": "content_asset"
 * }
 * ```
 *
 * Example response for folders:
 * ```json
 * {
 *     "resourceType": "library_folder",
 *     "resourceId": "support"
 * }
 * ```
 *
 * @param {parameters: {c_urlSegment: string}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} queryOptions
 * @returns {import("@tanstack/react-query").UseQueryResult<unknown, unknown>}
 */
export const useCustomUrlMapping = (apiOptions, queryOptions) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'url-mapping',
                    apiName: 'shopper-seo'
                },
                parameters: {
                    locale: locale.id,
                    ...apiOptions.parameters
                }
            },
            rawResponse: false
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useCustomUrlMapping', ...queryOptions?.meta}
        }
    )
}

/**
 * Custom api to get warranty configuration
 * @param {import("@tanstack/react-query").QueryOptions} queryOptions
 * @returns {import("@tanstack/react-query").UseQueryResult}
 */
export const useCustomWarrantyConfiguration = (queryOptions) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: 'warranties',
                    apiName: 'shopper-warranties'
                },
                parameters: {
                    locale: locale.id
                }
            },
            rawResponse: false
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useCustomWarrantyConfiguration', ...queryOptions?.meta}
        }
    )
}

/**
 * Custom mutation to check serial number
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 */
export const useCustomWarrantyCheckSerialNumberMutation = (mutationOptions) => {
    const {locale} = useMultiSite()
    return useImprovedCustomMutation(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    endpointPath: 'serial-number',
                    apiName: 'shopper-warranties'
                },
                parameters: {
                    locale: locale.id
                }
            }
        },
        mutationOptions
    )
}

/**
 * Custom mutation to register a warranty
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 */
export const useCustomWarrantyRegistrationMutation = (mutationOptions) => {
    const {locale} = useMultiSite()
    return useImprovedCustomMutation(
        {
            options: {
                method: 'POST',
                customApiPathParameters: {
                    endpointPath: 'register',
                    apiName: 'shopper-warranties'
                },
                parameters: {
                    locale: locale.id
                }
            }
        },
        mutationOptions
    )
}

/**
 * Hook to get slots HTML structure.
 * @param {{parameters: {slotId: string, context?: string}}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult<{c_data: string}, unknown>}
 */
export const useContentSlot = (
    {parameters: {slotId, context = 'global', contextId = ''}},
    queryOptions
) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-content',
                    endpointPath: `slot/${context}/${slotId}`
                },
                parameters: {
                    c_contextId: contextId,
                    locale: locale.id
                }
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useContentSlot', ...queryOptions?.meta},
            enabled: Boolean(slotId && slotId !== '-') && queryOptions?.enabled !== false
        }
    )
}

/**
 * Hook to get content assets modules as json list.
 * @param {{parameters: {assetId: string}}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult<Object[], unknown>}
 */
export const useContentAsset = ({parameters: {assetId, ...rest}}, queryOptions) => {
    const {locale} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-content',
                    endpointPath: `content/${assetId}`
                },
                parameters: {
                    locale: locale.id,
                    ...rest
                }
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useContentAsset', ...queryOptions?.meta},
            enabled: Boolean(assetId && assetId !== '-') && queryOptions?.enabled !== false
        }
    )
}

/**
 * Fetches configurator product highlights from the Shopper Configurator API based on the provided configuration ID and product ID.
 *
 * @function useConfiguratorHighlights
 * @param {Object} config - The configuration object containing API parameters.
 * @param {Object} config.parameters - Parameters for the API call.
 * @param {string} config.parameters.configId - The unique identifier for the configuration config object.
 * @param {string} config.parameters.productId - The unique identifier for the product.
 * @param {...Object} [config.parameters.rest] - Additional parameters to include in the request.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query.
 * @returns {import("@tanstack/react-query").UseQueryResult<{
 *    numberOfColors: number,
 *    numberOfAccessories: number,
 *    hasEngraving: boolean,
 * }, unknown>} - Result of the custom query including fetched data, loading state, and error state.
 */
export const useConfiguratorHighlights = (
    {parameters: {configId, productId, ...rest}},
    queryOptions
) => {
    const {locale, site} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-configurator',
                    endpointPath: `highlights/${configId}/${productId}`
                },
                parameters: {
                    locale: locale.id,
                    siteId: site.id,
                    ...rest
                }
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useConfiguratorHighlights', ...queryOptions?.meta},
            enabled: Boolean(configId && productId) && queryOptions?.enabled !== false
        }
    )
}

/**
 * Fetches configurator steps from the Shopper Configurator API based on the provided configuration ID.
 *
 * @function useConfiguratorSteps
 * @param {Object} config - The configuration object containing API parameters.
 * @param {Object} config.parameters - Parameters for the API call.
 * @param {string} config.parameters.configuratorConfigId - The unique identifier for the configuration config object.
 * @param {...Object} [config.parameters.rest] - Additional parameters to include in the request.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query.
 * @returns {import("@tanstack/react-query").UseQueryResult<('configurations'|'colors'|'accessories'|'engraving')[], unknown>} - Result of the custom query including fetched data, loading state, and error state.
 */
export const useConfiguratorSteps = (
    {parameters: {configuratorConfigId, ...rest}},
    queryOptions
) => {
    const {locale, site} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-configurator',
                    endpointPath: `steps/${configuratorConfigId}`
                },
                parameters: {
                    locale: locale.id,
                    siteId: site.id,
                    ...rest
                }
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useConfiguratorSteps', ...queryOptions?.meta},
            enabled: Boolean(configuratorConfigId) && queryOptions?.enabled !== false
        }
    )
}

/**
 * Fetches configurator configurations from the Shopper Configurator API based on the provided configuration ID.
 *
 * @function useConfiguratorConfigurations
 * @param {Object} config - The configuration object containing API parameters.
 * @param {Object} config.parameters - Parameters for the API call.
 * @param {string} config.parameters.configuratorConfigId - The unique identifier for the configuration config object.
 * @param {...Object} [config.parameters.rest] - Additional parameters to include in the request.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query.
 * @returns {import("@tanstack/react-query").UseQueryResult<Map<string, {
 *   label: string,
 *   items: Array<{
 *     id: string,
 *     name: string,
 *     slogan: string,
 *     slugUrl: string,
 *     c_imageStacked: {
 *       alt: string,
 *       disBaseLinks: string[],
 *     }
 *     imageThumbnail: string,
 *     orderable: boolean,
 *     selectors?: Array<{
 *       c_preferedVariantId: string,
 *       c_stackedImageBase: string,
 *       id: string,
 *       type: {set: boolean},
 *       variants: Array<{
 *         name: string,
 *         productId: string,
 *         variationValues: {
 *           color: string,
 *           name: string,
 *         }
 *       }>
 *     }>,
 *     setProducts?: Array<{
 *       selectors?: Array<{
 *         c_preferedVariantId: string,
 *         c_stackedImageBase: string,
 *         id: string,
 *         type: {set: boolean},
 *         variants: Array<{
 *           name: string,
 *           productId: string,
 *           variationValues: {
 *             color: string,
 *             name: string,
 *           }
 *         }>
 *       }>,
 *     }>
 *   }>
 * }>, unknown>} - Result of the custom query including fetched data, loading state, and error state.
 */
export const useConfiguratorConfigurations = (
    {parameters: {configuratorConfigId, ...rest}},
    queryOptions
) => {
    const {locale, site} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-configurator',
                    endpointPath: `configurations/${configuratorConfigId}`
                },
                parameters: {
                    locale: locale.id,
                    siteId: site.id,
                    ...rest
                }
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useConfiguratorConfigurations', ...queryOptions?.meta},
            enabled: Boolean(configuratorConfigId) && queryOptions?.enabled !== false
        }
    )
}

/**
 * Fetches configurator configurations from the Shopper Configurator API based on the provided configuration ID.
 *
 * @function useConfiguratorConfigurations
 * @param {Object} config - The configuration object containing API parameters.
 * @param {Object} config.parameters - Parameters for the API call.
 * @param {string} config.parameters.productId - The unique identifier for the configuration config object.
 * @param {...Object} [config.parameters.rest] - Additional parameters to include in the request.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query.
 * @returns {import("@tanstack/react-query").UseQueryResult<{
 *   id: string,
 *   name: string,
 *   slogan: string,
 *   slugUrl: string,
 *   c_imageStacked: {
 *     alt: string,
 *     disBaseLinks: string[],
 *   }
 *   imageThumbnail: string,
 *   engraving?: {
 *     selectors: Array<{
 *       c_preferedVariantId: string,
 *       c_stackedImageBase: string,
 *       id: string,
 *       type: {set: boolean},
 *       variants: Array<{
 *         name: string,
 *         productId: string,
 *         variationValues: {
 *           color: string,
 *           name: string,
 *         }
 *       }>
 *     }>,
 *   },
 *   accessories: Array<{
 *     selectors: Array<{
 *       c_preferedVariantId: string,
 *       c_stackedImageBase: string,
 *       id: string,
 *       type: {set: boolean},
 *       variants: Array<{
 *         name: string,
 *         productId: string,
 *         variationValues: {
 *           color: string,
 *           name: string,
 *         }
 *       }>
 *     }>,
 *   }>,
 *   setProducts: Array<{
 *     selectors: Array<{
 *       c_preferedVariantId: string,
 *       c_stackedImageBase: string,
 *       id: string,
 *       type: {set: boolean},
 *       variants: Array<{
 *         name: string,
 *         productId: string,
 *         variationValues: {
 *           color: string,
 *           name: string,
 *         }
 *       }>
 *     }>,
 *   }>,
 * }, unknown>} - Result of the custom query including fetched data, loading state, and error state.
 */
export const useConfiguratorStepData = ({parameters: {productId, ...rest}}, queryOptions) => {
    const {locale, site} = useMultiSite()
    return useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'shopper-configurator',
                    endpointPath: `summary/${productId}`
                },
                parameters: {
                    locale: locale.id,
                    siteId: site.id,
                    ...rest
                }
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            meta: {displayName: 'useConfiguratorStepData', ...queryOptions?.meta},
            enabled: Boolean(productId) && queryOptions?.enabled !== false
        }
    )
}
